import { useState } from 'react';
import {
    BUG_REPORT_CATEGORIES,
    BUG_REPORT_CATEGORY_LABELS,
    BUG_REPORT_LIMITS,
    type BugReportCategory,
    type BugReportContext,
} from '@sorrem/shared';
import { Button, Dropdown, DropdownItem, Modal, Panel, Textarea, useToast } from '@sorrem-client/ui/components';
import { useBugReport, useTabScreenshot } from '@sorrem-client/ui/hooks';
import { resolveBugReportContext } from '@sorrem-client/ui/telemetry';
import styles from './BugReportModal.module.scss';

interface BugReportModalProps {
    onClose: () => void;
}

/** How many characters of the report id a tester quotes. */
const SHORT_REPORT_ID_LENGTH = 8;

/**
 * A tester's bug report (#218): what happened, what they expected, and the
 * context attached for them, which they see in full before sending. The
 * context is read once, when the form opens, so it describes the moment the
 * bug was noticed. A screenshot of the whole tab is attached only when the
 * tester asks for one (#235); the form hides itself while it is taken.
 */
export function BugReportModal({ onClose }: BugReportModalProps): JSX.Element | null {
    const [context] = useState<BugReportContext>(resolveBugReportContext);
    const [category, setCategory] = useState<BugReportCategory>('gameplay');
    const [description, setDescription] = useState('');
    const [expected, setExpected] = useState('');
    const { isSending, error, submit } = useBugReport();
    const tabScreenshot = useTabScreenshot();
    const { showToast } = useToast();
    const canSend = description.trim().length > 0 && !isSending;

    const handleSend = async (): Promise<void> => {
        const reportId = await submit({
            category,
            description: description.trim(),
            ...(expected.trim().length > 0 ? { expected: expected.trim() } : {}),
            context,
            ...(tabScreenshot.screenshot ? { screenshot: tabScreenshot.screenshot.screenshot } : {}),
        });
        if (reportId !== null) {
            showToast({
                message: `Thanks, your report was sent. Reference ${reportId.slice(0, SHORT_REPORT_ID_LENGTH)} if you mention it on Discord.`,
                severity: 'info',
            });
            onClose();
        }
    };

    // Unrendered, not unmounted: the form keeps what the tester typed.
    if (tabScreenshot.isCapturing) {
        return null;
    }

    return (
        <Modal label="Report a bug" onClose={onClose} autoClose={false}>
            <Panel className={styles.bugReport} frame="thick">
                <h3 className={styles.title}>Report a bug</h3>

                <label className={styles.field} htmlFor="bug-report-category">
                    <span className={styles.label}>Kind of problem</span>
                    <Dropdown
                        id="bug-report-category"
                        value={category}
                        onChange={(event) => setCategory(event.target.value as BugReportCategory)}
                    >
                        {BUG_REPORT_CATEGORIES.map((id) => (
                            <DropdownItem key={id} value={id}>{BUG_REPORT_CATEGORY_LABELS[id]}</DropdownItem>
                        ))}
                    </Dropdown>
                </label>

                <label className={styles.field} htmlFor="bug-report-description">
                    <span className={styles.label}>What happened?</span>
                    <Textarea
                        id="bug-report-description"
                        value={description}
                        maxLength={BUG_REPORT_LIMITS.maxDescriptionLength}
                        placeholder="What you were doing, and what went wrong"
                        onChange={(event) => setDescription(event.target.value)}
                        autoFocus
                    />
                </label>

                <label className={styles.field} htmlFor="bug-report-expected">
                    <span className={styles.label}>What did you expect? (optional)</span>
                    <Textarea
                        id="bug-report-expected"
                        rows={2}
                        value={expected}
                        maxLength={BUG_REPORT_LIMITS.maxExpectedLength}
                        onChange={(event) => setExpected(event.target.value)}
                    />
                </label>

                <div className={styles.screenshot}>
                    {tabScreenshot.screenshot ? (
                        <>
                            <img className={styles.preview} src={tabScreenshot.screenshot.previewUrl} alt="Screenshot to send" />
                            <Button variant="secondary" onClick={tabScreenshot.remove}>Remove screenshot</Button>
                        </>
                    ) : (
                        <Button variant="secondary" onClick={() => void tabScreenshot.capture()}>Attach a screenshot</Button>
                    )}
                    {tabScreenshot.error !== null && <p className={styles.error} role="alert">{tabScreenshot.error}</p>}
                </div>

                <details className={styles.attached}>
                    <summary>Sent with your report</summary>
                    <dl className={styles.contextList}>
                        <dt>Release</dt>
                        <dd>{context.clientVersion}</dd>
                        <dt>Page</dt>
                        <dd>{context.route}</dd>
                        {context.crucibleId !== undefined && (
                            <>
                                <dt>Crucible</dt>
                                <dd>{context.crucibleId}</dd>
                            </>
                        )}
                        <dt>Window</dt>
                        <dd>{context.viewportWidth} x {context.viewportHeight} at {context.devicePixelRatio}x</dd>
                        {context.gpuRenderer !== undefined && (
                            <>
                                <dt>Graphics</dt>
                                <dd>{context.gpuRenderer}</dd>
                            </>
                        )}
                        {context.frameTiming !== undefined && (
                            <>
                                <dt>Frame rate</dt>
                                <dd>{context.frameTiming.fps.toFixed(0)} fps, {context.frameTiming.averageFrameTimeMs.toFixed(1)} ms per frame</dd>
                            </>
                        )}
                        <dt>Recent warnings and errors</dt>
                        <dd>{context.consoleTail.length === 0 ? 'None' : `${context.consoleTail.length} lines`}</dd>
                    </dl>
                    {context.consoleTail.length > 0 && (
                        <pre className={styles.consoleTail}>{context.consoleTail.join('\n')}</pre>
                    )}
                </details>

                {error !== null && <p className={styles.error} role="alert">{error}</p>}

                <div className={styles.actions}>
                    <Button variant="secondary" onClick={onClose}>Cancel</Button>
                    <Button onClick={() => void handleSend()} disabled={!canSend}>
                        {isSending ? 'Sending...' : 'Send report'}
                    </Button>
                </div>
            </Panel>
        </Modal>
    );
}
